<!--
/**
 * @copyright   OnlyOneProd © 2025
 * @authors     Jonathan COULLET <contact@onlyoneprod.com>
 * @link        https://onlyoneprod.com
 * @notice      All information contained herein is,
 *              and remains the property of OnlyOneProd and its suppliers,
 *              Any dissemination of this information or reproduction
 *              of this material is strictly forbidden unless prior
 *              written permission is obtained from OnlyOneProd.
 */
-->

<template>
    <section id="contact" class="screen secondary">
        <CommonShapeAnimation shape="polygon" :sides="7" :rotation="5" position="left" />
        <div class="content">
            <div class="wrapper">
                <div v-if="messageSent" class="panel success">
                    <div class="icon">
                        <IconComplete />
                    </div>
                    <div>
                        <h3>Votre message à bien été envoyé !</h3>
                        <p>Nous vous contacterons très prochainement.</p>
                    </div>
                </div>
                <div v-else class="panel grid-2 contact">
                    <div>
                        <header>
                            <IconMessages />
                            <h3>Nous contacter</h3>
                        </header>
                        <p><strong>Prêts à donner vie à vos projets ?</strong></p>
                        <p>Faites le premier pas vers une communication d’exception.</p>
                        <p>Remplissez le formulaire suivant et discutons de vos ambitions.</p>
                        <div class="error" v-if="messageError">
                            <p>Une erreur est survenue lors de l'envoi du message. Veuillez réessayer ultérieurement.</p>
                        </div>
                        <div class="error" v-if="Object.keys(validationErrors).length > 0">
                            <div v-for="(errors, field) in validationErrors" :key="field">
                                <p><strong>{{ field === 'name' ? 'Nom' : field === 'message' ? 'Message' : field }} :</strong></p>
                                <p v-for="error in errors" :key="error">{{ error }}</p>
                            </div>
                        </div>
                    </div>
                    <div>
                        <form @submit="submit">
                            <CommonInput name="contact_name" label="Nom / Organisation" v-model:value="name"
                                invalid="Veuillez entrer un nom valide." :required="true" :disabled="messageSending" />
                            <CommonInput name="contact_email" type="email" label="Adresse email" v-model:value="email"
                                invalid="Veuillez entrer une adresse email valide." :required="true"
                                :disabled="messageSending" />
                            <CommonInput name="contact_message" type="area" label="Message" v-model:value="message"
                                invalid="Veuillez entrer un message." :required="true" :disabled="messageSending" />
                            
                            <input 
                                type="text" 
                                name="website" 
                                v-model="honeypot" 
                                style="position: absolute; left: -9999px; opacity: 0; pointer-events: none;" 
                                tabindex="-1" 
                                autocomplete="off"
                                aria-hidden="true"
                            />
                            
                            <div class="actions">
                                <CommonButton :loading="messageSending" icon="send" type="submit">Envoyer</CommonButton>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
import CommonShapeAnimation from "@/components/common/CommonShapeAnimation.vue";
import CommonButton from "@/components/common/CommonButton.vue";
import CommonInput from "@/components/common/CommonInput.vue";

import IconComplete from "@/components/icons/IconComplete.vue";
import IconMessages from "@/components/icons/IconMessages.vue";

import { trackFormSubmit } from "@/helpers/analytics.helper";
import { SpamDetectionHelper } from "@/helpers/spam-detection.helper";

import { ref } from "vue";

const messageSending = ref(false);
const messageSent = ref(false);
const messageError = ref(false);
const validationErrors = ref<{ [key: string]: string[] }>({});

const name = ref("");
const email = ref("");
const message = ref("");
const honeypot = ref("");

const submit = async (event: Event) => {
    event.preventDefault();
    event.stopPropagation();

    messageError.value = false;
    validationErrors.value = {};

    const spamValidation = SpamDetectionHelper.validateContactForm({
        name: name.value,
        email: email.value,
        message: message.value
    });

    if(!spamValidation.isValid) {
        validationErrors.value = spamValidation.errors;
        return;
    }
    if(honeypot.value.trim() !== '') {
        messageError.value = true;
        return;
    }

    messageSending.value = true;

    try {
        const body = {
            name: name.value,
            email: email.value,
            message: message.value,
            honeypot: honeypot.value
        };
        
        const request = await fetch(`${process.env.VUE_APP_API_BASEURL}/mail/contact`, {
            method: "POST",
            headers: {
                'Content-Type': 'application/json',
                'Origin': process.env.VUE_APP_BASEURL
            },
            body: JSON.stringify(body)
        });

        messageSending.value = false;
        
        if(!request.ok) {
            if(request.status === 400) {
                const errorData = await request.json();
                if(errorData.errors) {
                    console.error('Validation errors:', errorData.errors);
                }
            }
            trackFormSubmit("contact");
            messageError.value = true;
            return;
        }
        
        messageSent.value = true;
        trackFormSubmit("contact");

    }catch (error) {
        console.error(error);
        messageSending.value = false;
        messageError.value = true;
    }
};
</script>

<style lang="sass">
</style>